import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../api';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import CreatorPageHeader from '../components/dashboard/CreatorPageHeader';

const CreatorBids = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [availableBids, setAvailableBids] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'creator') return;

    const fetchBids = async () => {
      try {
        const response = await api.get(`/creators/${user.userId}/bids`);
        const { availableBids, myBids } = response.data;
        setAvailableBids(availableBids || []);
        setMyBids(myBids || []);
      } catch (err) {
        // Fallback: try to get bidding opportunities from dashboard endpoint
        try {
          const dashboardResponse = await api.get(`/creators/${user.userId}/dashboard`);
          setAvailableBids(dashboardResponse.data.biddingOpportunities || []);
        } catch (dashboardErr) {
          // Dashboard fetch also failed
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBids();
  }, [user]);

  const handlePlaceBid = async (bidId, bidAmount) => {
    try {
      await api.post(`/bids/${bidId}/place`, { amount: bidAmount });
      alert('Bid placed successfully!');
      // Refresh the data
      window.location.reload();
    } catch (err) {
      alert('Failed to place bid. Please try again.');
    }
  };

  const getBidStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'accepted':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatRupees = (value) => {
    if (value === null || value === undefined || value === '') return '₹0';
    if (typeof value === 'number') return `₹${value.toLocaleString('en-IN')}`;
    const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(numeric)) return `₹${String(value).replace(/\$/g, '').trim()}`;
    return `₹${numeric.toLocaleString('en-IN')}`;
  };

  if (!user) return <p className="p-8 text-center">Please log in to view your bids.</p>;
  if (user.role !== 'creator') return <p className="p-8 text-center">Access denied. Only Creators can view this page.</p>;
  if (loading) return <p className="p-8 text-center">Loading bidding opportunities...</p>;

  return (
    <>
      <Helmet>
        <title>Bidding Opportunities - CollabOrb Creator</title>
        <meta name="description" content="Explore and place bids on available projects on CollabOrb creator platform" />
      </Helmet>
      
      <CreatorNavbar />
      
      <div className="pt-16 sm:pt-20">
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <CreatorPageHeader
          kicker="Opportunity workspace"
          title="Bidding Opportunities"
          description="Discover new campaign opportunities and track the status of bids you have already placed."
          actions={
            <button
              onClick={() => navigate('/dashboard/creator')}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Back to Dashboard
            </button>
          }
        />

        {/* Available Bidding Opportunities */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h2 className="text-2xl font-semibold mb-6 text-blue-600">Available Opportunities</h2>
          {availableBids.length > 0 ? (
            <div className="space-y-4">
              {availableBids.map((bid) => (
                <div key={bid.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{bid.projectTitle || bid.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{bid.brandName}</p>
                      {bid.description && (
                        <p className="text-sm text-gray-700 mb-3">{bid.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        {bid.deadline && (
                          <span className="text-red-600 font-medium">
                            📅 Deadline: {new Date(bid.deadline).toLocaleDateString()}
                          </span>
                        )}
                        {bid.budget && (
                          <span className="text-green-600 font-medium">💰 Budget: {formatRupees(bid.budget)}</span>
                        )}
                        {bid.reward && (
                          <span className="text-green-600 font-medium">🎁 Reward: {formatRupees(bid.reward)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <button
                        onClick={() => {
                          const bidAmount = prompt('Enter your bid amount:');
                          if (bidAmount && !isNaN(bidAmount)) {
                            handlePlaceBid(bid.id, parseFloat(bidAmount));
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        Place Bid
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <p className="text-gray-500 text-lg">No bidding opportunities available</p>
              <p className="text-gray-400 text-sm mt-2">Check back later for new projects to bid on</p>
            </div>
          )}
        </section>

        {/* My Bids */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h2 className="text-2xl font-semibold mb-6 text-green-600">My Bids</h2>
          {myBids.length > 0 ? (
            <div className="space-y-4">
              {myBids.map((bid) => (
                <div key={bid.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{bid.projectTitle || bid.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{bid.brandName}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-blue-600 font-medium">💰 My Bid: {formatRupees(bid.bidAmount)}</span>
                        {bid.bidDate && (
                          <span className="text-gray-600">📅 Placed: {new Date(bid.bidDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBidStatusColor(bid.status)}`}>
                        {bid.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">You haven't placed any bids yet</p>
              <p className="text-gray-400 text-sm mt-2">Browse available opportunities above to get started</p>
            </div>
          )}
        </section>
        </div>
      </div>
    </>
  );
};

export default CreatorBids;
